/** Max delay between two Space presses to count as a double-tap. */
const DOUBLE_TAP_MS = 250;

/**
 * Keyboard + mouse input with Pointer Lock. Movement uses physical key
 * positions (KeyW/KeyA/...) so it works on any keyboard layout.
 */
export class Input {
  forward = false;
  backward = false;
  left = false;
  right = false;
  sprint = false;
  jump = false;
  control = false;
  alt = false;

  /** Accumulated mouse movement since the last `consumeMouseDelta` call. */
  private mouseDX = 0;
  private mouseDY = 0;
  /** Accumulated wheel movement since the last `consumeWheelDelta` call. */
  private wheelDelta = 0;
  /** Mouse button presses (0 = left, 1 = middle, 2 = right) queued while locked. */
  private clicks: number[] = [];
  private readonly heldButtons = new Set<number>();
  private undoQueued = 0;
  private redoQueued = 0;

  /** Double-tap Space detection (fly mode toggle). */
  private lastSpacePress = 0;
  private flyToggleQueued = false;

  private locked = false;

  constructor(private readonly canvas: HTMLElement) {
    // Capture phase so Ctrl+W / Ctrl+T etc. don't hit the browser first.
    document.addEventListener('keydown', (e) => this.onKey(e, true), true);
    document.addEventListener('keyup', (e) => this.onKey(e, false), true);

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (!this.locked) this.reset();
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.mouseDX += e.movementX;
      this.mouseDY += e.movementY;
    });

    document.addEventListener('mousedown', (e) => {
      if (!this.locked) return;
      if (e.button === 0 || e.button === 1 || e.button === 2) {
        e.preventDefault();
        this.heldButtons.add(e.button);
        this.clicks.push(e.button);
      }
    });
    document.addEventListener('mouseup', (e) => {
      this.heldButtons.delete(e.button);
    });
    document.addEventListener('auxclick', (e) => {
      if (this.locked) e.preventDefault();
    });

    document.addEventListener('contextmenu', (e) => {
      if (this.locked) e.preventDefault();
    });

    document.addEventListener(
      'wheel',
      (e) => {
        if (!this.locked) return;
        e.preventDefault();
        this.wheelDelta += Math.sign(e.deltaY);
      },
      { passive: false },
    );
  }

  get isLocked(): boolean {
    return this.locked;
  }

  requestLock(): void {
    this.canvas.requestPointerLock();
  }

  onLockChange(callback: (locked: boolean) => void): void {
    document.addEventListener('pointerlockchange', () =>
      callback(document.pointerLockElement === this.canvas),
    );
  }

  consumeMouseDelta(): { dx: number; dy: number } {
    const delta = { dx: this.mouseDX, dy: this.mouseDY };
    this.mouseDX = 0;
    this.mouseDY = 0;
    return delta;
  }

  consumeWheelDelta(): number {
    const delta = this.wheelDelta;
    this.wheelDelta = 0;
    return delta;
  }

  /** True once per double-tap of Space. */
  consumeFlyToggle(): boolean {
    const toggled = this.flyToggleQueued;
    this.flyToggleQueued = false;
    return toggled;
  }

  consumeClicks(): number[] {
    if (this.clicks.length === 0) return this.clicks;
    const clicks = this.clicks;
    this.clicks = [];
    return clicks;
  }

  isButtonDown(button: number): boolean {
    return this.heldButtons.has(button);
  }

  consumeUndo(): boolean {
    if (this.undoQueued === 0) return false;
    this.undoQueued--;
    return true;
  }

  consumeRedo(): boolean {
    if (this.redoQueued === 0) return false;
    this.redoQueued--;
    return true;
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    if (!this.locked) return;
    // Block browser chrome shortcuts (Ctrl+W close tab, Ctrl+T, etc.) while playing.
    if (e.ctrlKey || e.metaKey || e.altKey) e.preventDefault();
    if (e.code === 'KeyZ' && down && !e.repeat && e.ctrlKey) {
      this.undoQueued++;
      return;
    }
    if (e.code === 'KeyY' && down && !e.repeat && e.ctrlKey) {
      this.redoQueued++;
      return;
    }
    switch (e.code) {
      case 'KeyW':
        this.forward = down;
        e.preventDefault();
        break;
      case 'KeyS':
        this.backward = down;
        e.preventDefault();
        break;
      case 'KeyA':
        this.left = down;
        e.preventDefault();
        break;
      case 'KeyD':
        this.right = down;
        e.preventDefault();
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.sprint = down;
        e.preventDefault();
        break;
      case 'ControlLeft':
      case 'ControlRight':
        this.control = down;
        e.preventDefault();
        break;
      case 'AltLeft':
      case 'AltRight':
        this.alt = down;
        e.preventDefault();
        break;
      case 'Space':
        if (down && !e.repeat) {
          const now = performance.now();
          if (now - this.lastSpacePress < DOUBLE_TAP_MS) {
            this.flyToggleQueued = true;
            this.lastSpacePress = 0;
          } else {
            this.lastSpacePress = now;
          }
        }
        this.jump = down;
        e.preventDefault();
        break;
    }
  }

  private reset(): void {
    this.forward = this.backward = this.left = this.right = false;
    this.sprint = this.jump = this.control = this.alt = false;
    this.clicks = [];
    this.heldButtons.clear();
    this.undoQueued = this.redoQueued = 0;
    this.flyToggleQueued = false;
    this.lastSpacePress = 0;
  }
}
